import React from "react";
import { css } from "glamor";
import Modal from "../library/Modal";
import formatDate from "../../utils/formatDate";
import { toBRLCurrency } from "../../utils/formatCurrency";
import { purchaseStatusVariant, paymentMethodText } from "./financePurchasesHelpers";

const modalClass = css({
  fontFamily: "Montserrat, sans-serif",
  "&.ui.small.modal": { maxWidth: 480, borderRadius: 10 },
  ".header": {
    borderBottom: "0.6px solid #E2E8F0 !important",
    padding: "16px 20px !important",
    fontFamily: "Montserrat, sans-serif !important",
  },
  ".content": { padding: "20px !important", fontFamily: "Montserrat, sans-serif !important" },
  ".actions": {
    display: "flex",
    justifyContent: "flex-end",
    borderTop: "0.6px solid #E2E8F0 !important",
    background: "#FFFFFF !important",
    padding: "12px 20px !important",
  },
});

const modalTitleClass = css({
  margin: 0,
  color: "#0F172A",
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "24px",
  fontFamily: "Montserrat, sans-serif !important",
});

const modalStatusRowClass = css({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 18,
});

const modalProductNameClass = css({
  color: "#0F172A",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "21px",
  overflowWrap: "anywhere",
});

const detailGridClass = css({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "14px 16px",
  "@media(max-width: 400px)": { gridTemplateColumns: "1fr", gap: 12 },
});

const detailFieldClass = css({ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 });
const detailLabelClass = css({ color: "#94A3B8", fontSize: 11, fontWeight: 400, lineHeight: "16px" });
const detailValueClass = css({ color: "#0F172A", fontSize: 13, fontWeight: 500, lineHeight: "20px", wordBreak: "break-word" });

const statusBadgeClass = (variant) =>
  css({
    display: "inline-flex",
    alignItems: "center",
    height: 22,
    padding: "0 8px",
    borderRadius: 4,
    border: `0.6px solid ${variant.border}`,
    background: variant.background,
    color: variant.color,
    fontSize: 11,
    fontWeight: 500,
    lineHeight: "16px",
    whiteSpace: "nowrap",
  });

const closeButtonClass = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 36,
  padding: "0 16px",
  border: "0.6px solid #E2E8F0",
  borderRadius: 6,
  background: "#FFFFFF",
  color: "#475569",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  ":hover": { background: "#F8FAFC" },
});

function DetailField({ label, value }) {
  return (
    <div className={detailFieldClass}>
      <span className={detailLabelClass}>{label}</span>
      <span className={detailValueClass}>{value || "—"}</span>
    </div>
  );
}

export default function PurchaseDetailsModal({ purchase, onClose }) {
  if (!purchase) return null;

  const status = purchaseStatusVariant(purchase);

  return (
    <Modal open={!!purchase} onClose={onClose} className={modalClass} size="small">
      <Modal.Header>
        <h2 className={modalTitleClass}>Detalhes da compra</h2>
      </Modal.Header>
      <Modal.Content>
        <div className={modalStatusRowClass}>
          <span className={modalProductNameClass}>{purchase.productName || "—"}</span>
          <span className={statusBadgeClass(status)}>{status.text}</span>
        </div>

        <div className={detailGridClass}>
          <DetailField
            label="Data"
            value={purchase.createdAt ? formatDate(purchase.createdAt, "DD/MM/YYYY") : null}
          />
          <DetailField label="Valor" value={toBRLCurrency(purchase.productPrice)} />
          <DetailField label="Método de pagamento" value={paymentMethodText(purchase.paymentType)} />
          <DetailField
            label="Serviço"
            value={
              purchase.paymentService
                ? purchase.paymentService.charAt(0).toUpperCase() + purchase.paymentService.slice(1)
                : null
            }
          />
          <DetailField label="Cód. da transação" value={purchase.transactionCode} />
          <DetailField label="ID da compra" value={String(purchase.id)} />
          {purchase.coursesUserExpiresAt && (
            <DetailField
              label="Acesso até"
              value={formatDate(purchase.coursesUserExpiresAt, "DD/MM/YYYY")}
            />
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <button type="button" className={closeButtonClass} onClick={onClose}>
          Fechar
        </button>
      </Modal.Actions>
    </Modal>
  );
}
